import lockr from 'utils/local-store';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { ColumnDef, OnChangeFn, SortingState } from '@tanstack/react-table';

import { FjFlexContainer, FjPaginator } from 'components/primitives';
import { Label, Select } from 'components/styled-primitives';

import * as CONSTANTS from '../../../constants';
import { buildColumns } from './column-builder';
import { ControlsWrapper, Table } from './components';
import LoadingScreen from './LoadingScreen';

// Column descriptors are a rich, consumer-defined domain shape (path/accessor/
// Header/Cell/sortMethod/width/…) consumed by the untyped `buildColumns`.
type SortTableColumn = Record<string, unknown>;
// Rows are arbitrary domain records keyed by column accessors.
type SortTableRow = Record<string, unknown>;

interface SortState {
  id: string;
  desc: boolean;
}

interface FetchDataParams {
  page: number;
  pageSize: number;
  sorted: SortState[];
}

const STORAGE_KEYS = CONSTANTS.LOCAL_STORAGE_KEYS;

// The consumer-facing onEdit receives exactly the payload the column builder
// emits from a cell edit; reuse that (untyped-import-derived) shape.
type OnEdit = Parameters<typeof buildColumns>[0]['handleEdit'];

// The Rows-per-page dropdown drives numeric page sizes, whereas the shared
// FjSelect API is typed for string values; expose the numeric contract this
// table actually uses via a locally-typed wrapper over the styled component.
interface RowsSelectProps {
  value: number;
  onChange: (value: number) => void;
  options: number[];
  disabled?: boolean;
}

const RowsSelect = styled(Select).attrs<{ disabled?: boolean }>(({ disabled, theme }) => ({
  ...(disabled && { bgColorIcon: theme.colors.tabBackground }),
}))`
  width: ${({ theme: { grid } }) => grid.units(3.25)};
` as unknown as React.ComponentType<RowsSelectProps>;

const Paginator = styled(FjPaginator).attrs(({ theme }) => ({
  color: theme.colors.selectableLightBg,
}))``;

const Container = styled(FjFlexContainer)`
  flex-direction: column;
  height: 100%;
  position: relative;
`;

interface FjSortTableProps {
  hidePagination?: boolean;
  filterControls?: React.ReactNode;
  controls?: React.ReactNode;
  theme: DefaultTheme;
  percentLoaded?: number;
  columns?: SortTableColumn[];
  data?: SortTableRow[];
  onEdit?: OnEdit;
  defaultPage?: number;
  defaultPageSize?: number;
  loading?: boolean;
  filter?: unknown;
  tableName?: string;
  disableControls?: boolean;
  pageSizeOptions?: number[];
  smallRows?: boolean;
  selectedRowsByID?: Record<string, boolean> | null;
  // react-table-6 controlled ("manual") mode: the consumer paginates/sorts the
  // data itself and is notified via onFetchData; `pages` is the total page count.
  manual?: boolean;
  onFetchData?: (params: FetchDataParams) => void;
  pages?: number;
  sortable?: boolean;
}

const FjSortTable = ({
  hidePagination = false,
  filterControls,
  controls,
  theme,
  percentLoaded = 100,
  columns,
  data = [],
  onEdit,
  defaultPage = 0,
  defaultPageSize,
  loading = false,
  filter,
  tableName = 'table',
  disableControls = false,
  pageSizeOptions = [5, 10, 20, 25, 50, 100],
  smallRows = false,
  selectedRowsByID,
  manual = false,
  onFetchData,
  pages,
  sortable = true,
}: FjSortTableProps): React.ReactElement => {
  // lockr.get is untyped (returns unknown); the stored values are a number and
  // a per-table sort map respectively.
  const memGlobalTableRecordsPerPage = lockr.get(
    STORAGE_KEYS.GLOBAL.USER.TABLE.ROWS_PER_PAGE,
  ) as number | undefined;

  const memTableSort: Record<string, SortState> =
    (lockr.get(STORAGE_KEYS.USER.TABLE.SORT) as Record<string, SortState>) || {};

  const [page, setPage] = useState<number>(defaultPage);
  const [pageSize, setPageSize] = useState<number>(
    defaultPageSize || memGlobalTableRecordsPerPage || 20,
  );

  // `useState`'s initializer only runs on mount, so if a consumer re-renders this same
  // (already-mounted) instance with a different `defaultPageSize` — e.g. toggling between two
  // views that pass different values, without remounting the table — `pageSize` would otherwise
  // stay stuck at whatever it was initialized to (including when `defaultPageSize` goes from set
  // back to undefined, which must fall through to the same memGlobalTableRecordsPerPage/20
  // default the initial useState computed). Only fires when `defaultPageSize` itself actually
  // changes between renders, so every existing consumer that passes a fixed value (or never
  // passes one) is completely unaffected — the effect just never re-runs for them.
  const isMountRef = useRef(true);
  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }
    setPageSize(defaultPageSize || memGlobalTableRecordsPerPage || 20);
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPageSize]);
  const [sortState, setSortState] = useState<SortState>(
    memTableSort[tableName] || { id: '', desc: false },
  );
  // The v8 sorting model starts empty so rows are NOT reordered on mount; the
  // persisted sort only restores the header indicator (via `sortState`). This
  // matches the prior react-table-6 behavior, which never applied the stored
  // sort to the row order on mount.
  const [sorting, setSorting] = useState<SortingState>([]);

  // Set (not cleared) here, consumed by the onFetchData effect below — see
  // that effect for why the two need to coordinate through a ref rather than
  // each independently reacting to [filter, sortState, pageSize].
  const pageResetPendingRef = useRef(false);
  useEffect(() => {
    if (page !== 0) pageResetPendingRef.current = true;
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, sortState, pageSize]);

  useEffect(() => {
    if (!defaultPageSize)
      lockr.set(STORAGE_KEYS.GLOBAL.USER.TABLE.ROWS_PER_PAGE, pageSize);
  }, [pageSize]);

  useEffect(() => {
    lockr.set(STORAGE_KEYS.USER.TABLE.SORT, {
      ...memTableSort,
      [tableName]: sortState,
    });
  }, [sortState]);

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    setSorting(next);
    setSortState(next[0] || { id: '', desc: false });
  };

  const renderColumns = useMemo(
    () => buildColumns({ columns, handleEdit: onEdit!, sortState }),
    [columns, onEdit, sortState],
  );

  const table = useReactTable<SortTableRow>({
    data,
    // buildColumns emits @tanstack v8 column defs typed as a minimal internal
    // shape; assert to the v8 ColumnDef union it structurally satisfies.
    columns: renderColumns as unknown as ColumnDef<SortTableRow>[],
    state: { sorting, pagination: { pageIndex: page, pageSize } },
    onSortingChange: handleSortingChange,
    enableSorting: sortable,
    enableSortingRemoval: true,
    // Per-column opt-out is carried through buildColumns' `enableResizing`;
    // react-table-6 defaulted every column to resizable, so mirror that here.
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    // react-table-6 always sorted ascending on the first click. v8 otherwise
    // infers desc-first for numeric columns, so pin asc-first for parity.
    sortDescFirst: false,
    // In manual mode the consumer sorts + paginates the data and provides the
    // total `pages`; the table only renders the page it was handed.
    manualSorting: manual,
    manualPagination: manual,
    ...(manual && { pageCount: pages ?? 1 }),
    getCoreRowModel: getCoreRowModel(),
    ...(!manual && { getSortedRowModel: getSortedRowModel() }),
    ...(!manual && { getPaginationRowModel: getPaginationRowModel() }),
  });

  // Manual mode: mirror react-table-6's onFetchData — notify the consumer of the
  // current page/size/sort on mount and whenever they change, so it can fetch +
  // paginate the matching slice. (The consumer's initial tableState.page is 1;
  // this mount fire corrects it to the table's 0-based page, as v6 did.)
  //
  // When filter/sortState/pageSize change while not on page 0, the reset
  // effect above schedules `setPage(0)` and arms `pageResetPendingRef` — this
  // effect runs in that same commit but `page` hasn't caught up yet, so it
  // waits rather than reporting a stale pre-reset page; the re-render where
  // `page` lands on 0 clears the ref and reports once, atomically, matching
  // react-table-6's behavior. (Deliberately NOT keyed on `filter` directly —
  // the connected wrapper re-syncs `filter` to a new-but-equal object shortly
  // after mount regardless of a real change, which would otherwise cause a
  // spurious extra fire; the reset effect already reacts to genuine filter
  // changes, and this effect just rides along via the ref.)
  useEffect(() => {
    if (!manual || !onFetchData) return;

    if (pageResetPendingRef.current) {
      if (page !== 0) return;
      pageResetPendingRef.current = false;
    }

    onFetchData({
      page,
      pageSize,
      sorted: sortState.id ? [{ id: sortState.id, desc: !!sortState.desc }] : [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manual, page, pageSize, sortState]);

  const pageCount = manual ? (pages ?? 1) : table.getPageCount();
  const canPrevious = page > 0;
  const canNext = page < pageCount - 1;
  const paginatorDisabled = !canNext && !canPrevious;

  // filterControls/controls are consumer-provided slots independent of the Rows/Page chrome
  // below — hidePagination only hides that chrome (this table's own client-side row-count/page
  // navigator), not a caller's search or action controls. ControlsWrapper itself is skipped
  // entirely when there's nothing to put in it (no controls, and the chrome is hidden), so
  // hidePagination with no controls renders no empty row.
  return (
    <Container>
      {filterControls || null}
      {(controls || !hidePagination) && (
        <ControlsWrapper data-testid="table-controls-wrapper">
          <FjFlexContainer>{controls}</FjFlexContainer>
          {!hidePagination && (
            <>
              <Label disabled={disableControls}>Rows:</Label>
              <RowsSelect
                value={pageSize}
                onChange={(newPageSize) => setPageSize(newPageSize)}
                options={pageSizeOptions}
                disabled={disableControls}
              />
              <Label disabled={paginatorDisabled}>Page:</Label>
              <Paginator
                label={page + 1}
                onClickLeft={() => setPage(page - 1)}
                onClickRight={() => setPage(page + 1)}
                disabledLeft={!canPrevious}
                disabledRight={!canNext}
                color={theme.colors.selectableLightBg}
              />
              <Label disabled={paginatorDisabled}>of {pageCount}</Label>
            </>
          )}
        </ControlsWrapper>
      )}
      <Table table={table} selectedRowsByID={selectedRowsByID} smallRows={smallRows} />
      <LoadingScreen percent={percentLoaded} loading={loading} />
    </Container>
  );
};

export default FjSortTable;
